import { ReportIndicators } from "./ReportIndicators";
import { ReactNode, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFilter } from "../../../hooks/useFilter";
import {
  flattenSelection,
  selectionToSortedIdsKey,
  useFilterContextV2,
} from "../../../contexts/FilterContextV2";
import {
  ReportExportContext,
  useReportLoading,
} from "../../../contexts/ReportLoadingContext";
import { DropdownV2, MultiDropdownV2 } from "../../FormControlsV2";
import { InfoCattleMap } from "../ViewDatas/InfoCattleMap";
import { InfoBatches } from "../ViewDatas/InfoBatches";
import { InfoCarbon } from "../ViewDatas/InfoCarbon";
import { InfoManagements } from "../ViewDatas/InfoManagements";
import "./styles.css";

export function useManagementSelection() {
  const selection = useFilterContextV2();
  const selectedFarms = useMemo(
    () => flattenSelection(selection.selectedFarms).slice(0, 1),
    [selection.selectedFarms],
  );
  const selectedAreas = useMemo(
    () =>
      flattenSelection(selection.selectedAreas).filter(
        (area) =>
          String(area.info?.farm?.id) === String(selectedFarms[0]?.value),
      ),
    [selection.selectedAreas, selectedFarms],
  );
  return { ...selection, selectedFarms, selectedAreas };
}

export function ReportTop({
  icon,
  title,
  subtitle,
  children,
  kind,
  multipleManagementFarms = false,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
  kind?: "cattle" | "batches" | "carbon" | "management";
  multipleManagementFarms?: boolean;
}) {
  const { t } = useTranslation();
  const { loading, error, retry } = useReportLoading();
  const { responseFarms, responseArea, paddockMapCatalogLoadError } =
    useFilter();
  const {
    selectedFarms,
    selectedAreas,
    setSelectedFarms,
    setSelectedAreas,
    catalogsReady,
    reloadCatalogs,
  } = useFilterContextV2();
  const farms = useMemo(
    () =>
      responseFarms.map((farm) => ({
        label: farm.name,
        value: farm.id,
        info: farm,
      })),
    [responseFarms],
  );
  const singleManagementFarm = kind === "management" && !multipleManagementFarms;
  const farmValues = flattenSelection(selectedFarms);
  const effectiveFarms =
    singleManagementFarm ? farmValues.slice(0, 1) : farmValues;
  const farmKey = selectionToSortedIdsKey(selectedFarms);
  const availableAreas = useMemo(
    () =>
      responseArea
        .filter((area) =>
          flattenSelection(selectedFarms).some(
            (farm) => String(farm.value) === String(area.farm?.id),
          ),
        )
        .map((area) => ({ label: area.name, value: area.id, info: area })),
    [responseArea, selectedFarms],
  );
  const effectiveAreas =
    singleManagementFarm
      ? availableAreas.filter(
          (area) =>
            String(area.info.farm?.id) === String(effectiveFarms[0]?.value),
        )
      : availableAreas;
  const areaValues = flattenSelection(selectedAreas).filter((area) =>
    effectiveAreas.some(
      (option) => String(option.value) === String(area.value),
    ),
  );

  useEffect(() => {
    if (!catalogsReady || paddockMapCatalogLoadError) return;
    if (selectedFarms === null && farms.length) setSelectedFarms(farms);
  }, [
    catalogsReady,
    paddockMapCatalogLoadError,
    selectedFarms,
    farms,
    setSelectedFarms,
  ]);
  useEffect(() => {
    if (!catalogsReady || paddockMapCatalogLoadError || selectedFarms === null)
      return;
    if (selectedAreas === null) {
      setSelectedAreas(availableAreas);
      return;
    }
    const valid = flattenSelection(selectedAreas).filter((area) =>
      availableAreas.some(
        (option) => String(option.value) === String(area.value),
      ),
    );
    if (valid.length !== flattenSelection(selectedAreas).length)
      setSelectedAreas(valid);
  }, [
    catalogsReady,
    paddockMapCatalogLoadError,
    farmKey,
    availableAreas,
    selectedAreas,
    selectedFarms,
    setSelectedAreas,
  ]);

  const busy = loading || !catalogsReady;
  const failed = error || paddockMapCatalogLoadError;
  return (
    <section className={`report-top ${kind ? "report-top--indicators" : ""}`}>
      <header className="report-heading">
        <div className="cattleMap_header">
          {icon}
          <div>
            <h2 className="cattleMap_title">{title}</h2>
            <h3 className="cattleMap_subtitle">{subtitle}</h3>
          </div>
        </div>
      </header>
      {kind && (
        <ReportIndicators busy={busy}>
          {kind === "cattle" && <InfoCattleMap />}
          {kind === "batches" && <InfoBatches />}
          {kind === "carbon" && <InfoCarbon />}
          {kind === "management" && <InfoManagements />}
        </ReportIndicators>
      )}
      <section
        className="report-filters"
        aria-label={t("reportLayout.filters")}
        aria-busy={busy}
      >

        <div className="report-filters__selection">
          {singleManagementFarm ? (
            <DropdownV2
              menuMount="overlay"
              name="report-farms"
              label={t("reportLayout.farms")}
              options={farms}
              value={effectiveFarms[0] ?? null}
              disabled={!catalogsReady}
              onChange={(option) => {
                setSelectedFarms(
                  option
                    ? [
                        option,
                        ...farmValues.filter(
                          (farm) => String(farm.value) !== String(option.value),
                        ),
                      ]
                    : [],
                );
                setSelectedAreas(
                  option
                    ? [
                        ...flattenSelection(selectedAreas).filter(
                          (area) =>
                            String(area.info?.farm?.id) !== String(option.value),
                        ),
                        ...responseArea
                          .filter((area) => String(area.farm?.id) === String(option.value))
                          .map((area) => ({ label: area.name, value: area.id, info: area })),
                      ]
                    : [],
                );
              }}
            />
          ) : (
            <MultiDropdownV2
              menuMount="overlay"
              name="report-farms"
              label={t("reportLayout.farms")}
              options={farms}
              value={farmValues}
              selectAll
              searchable
              disabled={!catalogsReady}
              onChange={(options, action) => {
                setSelectedFarms(options);
                const farmsToSelect = options.filter(
                  (farm) => action === "selectAll" || !farmValues.some(
                    (selected) => String(selected.value) === String(farm.value),
                  ),
                );
                const selectedAreaIds = new Set(
                  flattenSelection(selectedAreas).map((area) => String(area.value)),
                );
                setSelectedAreas(
                  responseArea
                    .filter((area) =>
                      options.some((farm) => String(farm.value) === String(area.farm?.id)) &&
                      (selectedAreaIds.has(String(area.id)) || farmsToSelect.some(
                        (farm) => String(farm.value) === String(area.farm?.id),
                      )),
                    )
                    .map((area) => ({ label: area.name, value: area.id, info: area })),
                );
              }}
            />
          )}
          <MultiDropdownV2
            menuMount="overlay"
            name="report-areas"
            label={t("reportLayout.areas")}
            options={effectiveAreas}
            value={areaValues}
            selectAll
            searchable
            disabled={!catalogsReady || !effectiveFarms.length}
            onChange={(options) =>
              setSelectedAreas(
                singleManagementFarm
                  ? [
                      ...flattenSelection(selectedAreas).filter(
                        (area) =>
                          String(area.info?.farm?.id) !==
                          String(effectiveFarms[0]?.value),
                      ),
                      ...options,
                    ]
                  : options,
              )
            }
          />
        </div>
        <ReportExportContext.Provider
          value={busy || failed || !effectiveFarms.length || !areaValues.length}
        >
          <div className="report-filters__specific">{children}</div>
        </ReportExportContext.Provider>
        {busy && <div className="report-progress" aria-hidden="true" />}
      </section>
    </section>
  );
}
